import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowDown, PackageX, UserX, TrendingUp, DoorClosed, Clock, ShieldAlert,
  ShieldCheck, Clock4, BellRing, QrCode, Unlock, Cloud, PackageCheck, Truck, ScanLine, Boxes, BellDot, Hand,
} from "lucide-react";
import { Media, Reveal, MaskedLines, SectionLabel } from "./Placeholder";
import { useLang } from "../i18n";
import { IMG } from "../assets";
import { scrollToId as scrollTo } from "../lenis";

/* ---------------- HERO ---------------- */
export function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <section ref={ref} id="home" className="relative min-h-screen overflow-hidden bg-white pt-16 sm:pt-20" data-testid="hero-section">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-10 px-5 py-12 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:py-16">
        <motion.div style={{ y: yText }} className="lg:col-span-6">
          <Reveal>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-1.5">
              <span className="h-2 w-2 animate-pulse rounded-full bg-electric-blue" />
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-ink/70">{t.hero.badge}</span>
            </div>
          </Reveal>

          <h1 className="font-display text-5xl font-black leading-[0.95] tracking-normal text-ink sm:text-7xl lg:text-[5.5rem]">
            <MaskedLines lines={[t.hero.title1]} />
            <MaskedLines lines={[t.hero.title2]} lineClassName="text-electric-blue" />
          </h1>

          <Reveal delay={0.3}>
            <p className="mt-7 max-w-md font-body text-lg font-medium leading-relaxed text-ink/70 sm:text-xl">
              {t.hero.subtitle}
            </p>
          </Reveal>

          <Reveal delay={0.45}>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <button onClick={() => scrollTo("solutions")} className="group flex items-center justify-center gap-2 rounded-md bg-ink px-7 py-4 font-grotesk text-base font-bold text-white transition-colors duration-300 hover:bg-electric-blue" data-testid="hero-cta-primary">
                {t.hero.cta1}
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </button>
              <button onClick={() => scrollTo("contact")} className="flex items-center justify-center gap-2 rounded-md border-2 border-ink bg-white px-7 py-4 font-grotesk text-base font-bold text-ink transition-colors duration-300 hover:bg-lime-accent" data-testid="hero-cta-secondary">
                {t.hero.cta2}
              </button>
            </div>
          </Reveal>
        </motion.div>

        <div className="relative lg:col-span-6">
          <motion.div style={{ y }} className="relative">
            <Media src={IMG.hero} ratio="4/5" className="w-full" />
            <div className="absolute -bottom-5 -left-5 hidden rounded-md bg-lime-accent px-5 py-3 font-grotesk text-sm font-extrabold text-ink shadow-[6px_6px_0px_#0A0A0A] sm:block">
              24/7 · SMART · SECURE
            </div>
          </motion.div>
        </div>
      </div>

      <button onClick={() => scrollTo("about")} className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 animate-bounce text-ink/40 lg:block" aria-label="scroll" data-testid="hero-scroll-indicator">
        <ArrowDown className="h-6 w-6" />
      </button>
    </section>
  );
}

/* ---------------- MARQUEE ---------------- */
export function Marquee() {
  const { t } = useLang();
  const text = t.marquee;
  return (
    <section className="overflow-hidden border-y border-ink bg-electric-blue py-5" data-testid="marquee">
      <div className="marquee-track">
        {[0, 1].map((k) => (
          <span key={k} className="font-display text-3xl font-black tracking-tight text-white sm:text-5xl">
            {Array(3).fill(text).map((s, i) => (
              <span key={i} className="mx-6">{s}</span>
            ))}
          </span>
        ))}
      </div>
    </section>
  );
}

/* ---------------- ABOUT / MANIFESTO ---------------- */
export function About() {
  const { t } = useLang();
  return (
    <section id="about" className="bg-white py-24 sm:py-32" data-testid="about-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <SectionLabel>{t.about.label}</SectionLabel>
            <Reveal>
              <h2 className="max-w-2xl font-sans text-4xl font-extrabold leading-tight tracking-tight text-ink sm:text-5xl lg:text-6xl">
                {t.about.title}
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-xl font-body text-lg leading-relaxed text-ink/70">{t.about.p1}</p>
              <p className="mt-4 max-w-xl font-body text-lg leading-relaxed text-ink/70">{t.about.p2}</p>
            </Reveal>

            <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-3">
              {t.about.chapters.map((c, i) => (
                <Reveal key={i} delay={i * 0.1} className="bg-white p-6">
                  <span className="font-display text-4xl font-black text-electric-blue">{c.n}</span>
                  <h3 className="mt-3 font-grotesk text-lg font-bold text-ink">{c.t}</h3>
                  <p className="mt-2 font-body text-sm leading-relaxed text-ink/60">{c.d}</p>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <Media src={IMG.about} ratio="4/5" className="w-full" />
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- PROBLEM ---------------- */
const problemIcons = [PackageX, UserX, TrendingUp, DoorClosed, Clock, ShieldAlert];
export function Problem() {
  const { t } = useLang();
  return (
    <section className="bg-ink py-24 text-white sm:py-32" data-testid="problem-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <SectionLabel dark>{t.problem.label}</SectionLabel>
            <Reveal>
              <h2 className="max-w-md font-sans text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
                {t.problem.title}
              </h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-white/10 bg-white/10 sm:grid-cols-2">
            {t.problem.items.map((it, i) => {
              const Icon = problemIcons[i];
              return (
                <Reveal key={i} delay={(i % 2) * 0.1} className="bg-ink p-7">
                  <Icon className="h-8 w-8 text-lime-accent" strokeWidth={1.5} />
                  <h3 className="mt-5 font-grotesk text-lg font-bold">{it.t}</h3>
                  <p className="mt-2 font-body text-sm leading-relaxed text-white/60">{it.d}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- SOLUTION ---------------- */
const solIcons = [ShieldCheck, Clock4, BellRing, QrCode, Unlock, Cloud];
export function Solution() {
  const { t } = useLang();
  return (
    <section id="solutions" className="bg-white py-24 sm:py-32" data-testid="solution-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="max-w-2xl">
          <SectionLabel>{t.solution.label}</SectionLabel>
          <Reveal>
            <h2 className="font-sans text-4xl font-extrabold leading-tight tracking-tight text-ink sm:text-5xl lg:text-6xl">
              {t.solution.title}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 font-body text-lg leading-relaxed text-ink/70">{t.solution.intro}</p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <Reveal className="lg:col-span-5">
            <Media src={IMG.solution} ratio="3/4" className="w-full" />
          </Reveal>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:col-span-7">
            {t.solution.items.map((it, i) => {
              const Icon = solIcons[i];
              return (
                <Reveal key={i} delay={(i % 2) * 0.08} className="group rounded-lg border border-border bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-ink hover:shadow-[6px_6px_0px_#0033FF]">
                  <div className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary transition-colors group-hover:bg-electric-blue">
                    <Icon className="h-5 w-5 text-ink transition-colors group-hover:text-white" strokeWidth={1.75} />
                  </div>
                  <h3 className="mt-5 font-grotesk text-lg font-bold text-ink">{it.t}</h3>
                  <p className="mt-2 font-body text-sm leading-relaxed text-ink/60">{it.d}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- HOW IT WORKS ---------------- */
const howIcons = [PackageCheck, Truck, ScanLine, Boxes, BellDot, Hand];
export function HowItWorks() {
  const { t } = useLang();
  return (
    <section id="how" className="bg-secondary py-24 sm:py-32" data-testid="how-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="mb-16 max-w-2xl">
          <SectionLabel>{t.how.label}</SectionLabel>
          <Reveal>
            <h2 className="font-sans text-4xl font-extrabold leading-tight tracking-tight text-ink sm:text-5xl lg:text-6xl">
              {t.how.title}
            </h2>
          </Reveal>
        </div>

        <div className="relative grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {t.how.steps.map((s, i) => {
            const Icon = howIcons[i];
            return (
              <Reveal key={i} delay={i * 0.08} className="relative rounded-lg border border-border bg-white p-7 transition-transform duration-300 hover:-translate-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-display text-5xl font-black text-ink/10">0{i + 1}</span>
                  <div className="flex h-12 w-12 items-center justify-center rounded-md bg-ink">
                    <Icon className="h-6 w-6 text-lime-accent" strokeWidth={1.5} />
                  </div>
                </div>
                <h3 className="mt-6 font-grotesk text-xl font-bold text-ink">{s.t}</h3>
                <p className="mt-2 font-body text-sm leading-relaxed text-ink/60">{s.d}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
